import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import {
  FootEnum,
  PlayerCardEntry,
  PlayerCareerEntry,
  PlayerSeason,
  StatisticKeyEnum,
  TeamPosition
} from "../client/api";
import {
  selectIsPlayerLoaded,
  selectPlayerById,
  selectPlayerCardById,
  selectPlayerCareerById,
  selectPlayerMatchesById,
  selectPlayerSeasonStats,
  selectPlayerStatus
} from "../selectors";
import { calculateAge, intlLang, mapToMatchSmallSmall } from "../config/utils";
import { translate } from "../config/translator";
import { LastMatchData, PlayerStats } from "../config/types";
import { fetchAllPlayerData, fetchPlayerSeason } from "../store/actions/playerActions";
import { PlayerPageActions } from "../store/actions/types";
import { ClavaContext } from "../config/contexts";

export type { PlayerStats, LastMatchData };

export const usePlayerPageController = (playerId: number | undefined) => {
  const dispatch = useDispatch<Dispatch<PlayerPageActions>>();
  const { l } = useContext(ClavaContext);

  const player = useSelector(selectPlayerById(playerId ?? 0));
  const playerCard = useSelector(selectPlayerCardById(playerId ?? 0));
  const playerCareer = useSelector(selectPlayerCareerById(playerId ?? 0));
  const playerMatches = useSelector(selectPlayerMatchesById(playerId ?? 0));
  const status = useSelector(selectPlayerStatus);
  const isLoaded = useSelector(selectIsPlayerLoaded(playerId ?? 0));

  const loading = status === "loading" && !isLoaded;

  useEffect(() => {
    if (!playerId) return;
    if (!isLoaded) {
      fetchAllPlayerData(dispatch, playerId);
    }
  }, [playerId, dispatch, isLoaded]);

  const mainTeam = useMemo(() => {
    if (!player?.teamPositions?.length) return null;
    const mainPosition = player.mainTeamId
      ? player.teamPositions.find(
        (tp: TeamPosition) => tp.team.id === player.mainTeamId
      )
      : player.teamPositions[0];
    return mainPosition?.team ?? player.teamPositions[0]?.team ?? null;
  }, [player]);

  const mainTeamPosition = useMemo(() => {
    if (!player?.teamPositions?.length) return null;
    const mainPosition = player.mainTeamId
      ? player.teamPositions.find(
        (tp: TeamPosition) => tp.team.id === player.mainTeamId
      )
      : player.teamPositions[0];
    return mainPosition ?? player.teamPositions[0] ?? null;
  }, [player]);

  const mainLeague = useMemo(() => {
    if (!mainTeamPosition?.team?.leagues?.length) return null;
    return mainTeamPosition.team.leagues[0];
  }, [mainTeamPosition]);

  const mainLeagueId = mainLeague?.id ?? null;

  const playerSeasonStats = useSelector(
    selectPlayerSeasonStats(playerId ?? 0, mainLeagueId ?? 0)
  );

  useEffect(() => {
    if (!playerId || !mainLeagueId) return;
    if (!playerSeasonStats) {
      fetchPlayerSeason(dispatch, playerId, mainLeagueId);
    }
  }, [playerId, mainLeagueId, dispatch, playerSeasonStats]);

  const shouldShowRating = useMemo(
    () => !!player?.rating,
    [player]
  );

  const stats = useMemo((): PlayerStats | null => {
    if (!playerCard) return null;

    const getStatValue = (key: StatisticKeyEnum): string | null => {
      const entry = playerCard.find(
        (e: PlayerCardEntry) => e.statistic.key === key
      );
      return entry?.value ?? null;
    };

    const goals = getStatValue(StatisticKeyEnum.PLAYER_MOST_GOALS);
    const assists = getStatValue(StatisticKeyEnum.PLAYER_MOST_ASSISTS);
    const start11 = getStatValue(StatisticKeyEnum.PLAYER_MOST_START_11);
    const changeIn = getStatValue(StatisticKeyEnum.PLAYER_MOST_CHANGE_IN);
    const changeOut = getStatValue(StatisticKeyEnum.PLAYER_MOST_CHANGE_OUT);
    const minutes = getStatValue(StatisticKeyEnum.PLAYER_MOST_MINUTES);
    const motm = getStatValue(StatisticKeyEnum.PLAYER_MOST_MOTM_AWARDS);
    const totw = getStatValue(StatisticKeyEnum.PLAYER_MOST_TOTW_AWARDS);

    const start11Num = start11 ? parseInt(start11, 10) : 0;
    const changeInNum = changeIn ? parseInt(changeIn, 10) : 0;
    const matches = start11Num + changeInNum;

    return {
      goals,
      assists,
      matches: matches > 0 ? matches.toString() : null,
      lineup: start11,
      minutes,
      motm,
      totw,
      changeIn,
      changeOut
    };
  }, [playerCard]);

  const seasonStats = useMemo(
    (): PlayerSeason[] | null => playerSeasonStats,
    [playerSeasonStats]
  );

  const lastMatch = useMemo((): LastMatchData | null => {
    if (!playerMatches.length) return null;
    const pm = playerMatches[0];
    return {
      matchElement: mapToMatchSmallSmall(pm.match),
      playerData: {
        amountMinutesPlayed: pm.amountMinutesPlayed,
        rating: pm.rating,
        manOfTheMatch: pm.manOfTheMatch,
        amountYellowCards: pm.amountYellowCards,
        amountRedCards: pm.amountRedCards,
        amountGoals: pm.amountGoals
      }
    };
  }, [playerMatches]);

  const playerName = player
    ? `${player.givenName} ${player.familyName}`
    : translate("loading", l);

  const age = useMemo(
    () => calculateAge(player?.birthdate),
    [player?.birthdate]
  );

  const footLabel = useMemo(() => {
    if (!player?.foot) return null;
    switch (player.foot) {
      case FootEnum.LEFT:
        return translate("left", l);
      case FootEnum.RIGHT:
        return translate("right", l);
      case FootEnum.BOTH:
        return translate("both", l);
      default:
        return null;
    }
  }, [player?.foot, l]);

  const formatCareerPeriod = useMemo(
    () =>
      (dateFrom: string, dateTo: string | null): string => {
        const fromDate = new Date(dateFrom);
        const toDate = dateTo ? new Date(dateTo) : new Date();
        const isOngoing = !dateTo;

        const fromMonth = fromDate.toLocaleDateString(intlLang(l), {
          month: "long",
          year: "numeric"
        });
        const toMonth = toDate.toLocaleDateString(intlLang(l), {
          month: "long",
          year: "numeric"
        });

        if (isOngoing) {
          return `${fromMonth} - ${translate("today", l)}`;
        }
        return `${fromMonth} - ${toMonth}`;
      },
    [l]
  );

  const seniorCareer = useMemo(
    () =>
      playerCareer?.senior?.filter(
        (entry: PlayerCareerEntry) => !entry.hidden
      ) || [],
    [playerCareer]
  );

  const youthCareer = useMemo(
    () =>
      playerCareer?.youth?.filter(
        (entry: PlayerCareerEntry) => !entry.hidden
      ) || [],
    [playerCareer]
  );

  return {
    l,
    player,
    loading,
    playerName,
    mainTeam,
    mainTeamPosition,
    mainLeague,
    shouldShowRating,
    stats,
    seasonStats,
    lastMatch,
    age,
    footLabel,
    formatCareerPeriod,
    seniorCareer,
    youthCareer
  };
};
